import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { toggleAttended, fetchBatchEventStatus, getIdToken } from '../attendedUtils';
import { UploadModal } from './MyImages';

const INFO_TABS = ['Show Stats', 'Bio', 'Links', 'Photos', 'Projects', 'Additional Info'];
const PARENT_TABS = ['Show Archive', 'Shows by Year', 'Stages', 'Promoters', 'Festivals', 'Artists Hosted', 'Flyers', 'Analytics'];
const FLYERS_SUBTABS = ['Flyer History', 'Shows Without Flyers'];
const PAGE_SIZE = 25;
const FLYER_PAGE_SIZE = 50;
const FLYER_COLS = 5;

const FLYER_SORT_OPTIONS = [
    { value: 'date_desc', label: 'Date (Most Recent to Oldest)' },
    { value: 'date_asc',  label: 'Date (Oldest to Most Recent)' },
    { value: 'att_desc',  label: 'Attendance (Most to Least)' },
    { value: 'att_asc',   label: 'Attendance (Least to Most)' },
];

// ── Pagination ────────────────────────────────────────────────────────────────
function Pagination({ currentPage, totalItems, onPageChange }) {
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    if (totalPages <= 1) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return (
        <div style={styles.pagination}>
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} style={{ ...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {}) }}>‹ Prev</button>
            {pages.map(p => (
                <button key={p} onClick={() => onPageChange(p)} style={{ ...styles.pageBtn, ...(p === currentPage ? styles.pageBtnActive : {}) }}>{p}</button>
            ))}
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ ...styles.pageBtn, ...(currentPage === totalPages ? styles.pageBtnDisabled : {}) }}>Next ›</button>
        </div>
    );
}

function FlyerPagination({ currentPage, totalItems, onPageChange }) {
    const totalPages = Math.ceil(totalItems / FLYER_PAGE_SIZE);
    if (totalPages <= 1) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return (
        <div style={styles.pagination}>
            <button onClick={() => { onPageChange(currentPage - 1); window.scrollTo(0, 0); }} disabled={currentPage === 1} style={{ ...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {}) }}>‹ Prev</button>
            {pages.map(p => (
                <button key={p} onClick={() => { onPageChange(p); window.scrollTo(0, 0); }} style={{ ...styles.pageBtn, ...(p === currentPage ? styles.pageBtnActive : {}) }}>{p}</button>
            ))}
            <button onClick={() => { onPageChange(currentPage + 1); window.scrollTo(0, 0); }} disabled={currentPage === totalPages} style={{ ...styles.pageBtn, ...(currentPage === totalPages ? styles.pageBtnDisabled : {}) }}>Next ›</button>
        </div>
    );
}

function SubTabBar({ tabs, active, onChange }) {
    return (
        <div style={styles.subTabBar}>
            {tabs.map((tab, i) => (
                <button
                    key={tab}
                    onClick={() => onChange(i)}
                    style={{ ...styles.subTabBtn, ...(active === i ? styles.subTabBtnActive : {}) }}
                >
                    {tab}
                </button>
            ))}
        </div>
    );
}

// ── Projects Tab ───────────────────────────────────────────────────────────────
function ProjectsTab({ projects }) {
    if (!projects || projects.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ color: '#666', fontSize: '14px', marginBottom: '12px' }}>No projects yet.</div>
                <a href="/add-project" style={{ color: '#bb86fc', fontSize: '13px', textDecoration: 'none' }}>+ Start a Project</a>
            </div>
        );
    }
    return (
        <div>
            {projects.map(p => (
                <div key={p.id} style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #2a2a2a' }}>
                    <div style={{ marginBottom: '6px' }}>
                        <a href={`/projects/${p.id}`} style={{ color: '#bb86fc', textDecoration: 'none', fontWeight: '600', fontSize: '14px' }}>{p.name}</a>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ flex: 1, height: '8px', backgroundColor: '#2c2c2c', borderRadius: '4px', overflow: 'hidden', maxWidth: '200px' }}>
                            <div style={{ width: `${Math.min(p.pct_complete, 100)}%`, height: '100%', backgroundColor: '#bb86fc', borderRadius: '4px' }} />
                        </div>
                        <span style={{ fontSize: '12px', color: '#bb86fc', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {p.pct_complete > 100 ? '100+%' : `${p.pct_complete}%`}
                        </span>
                        <span style={{ fontSize: '12px', color: '#555' }}>{p.actual_value} / {p.estimated_value} shows</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#555', marginTop: '4px' }}>{p.metric} · {p.date_start} to {p.date_end}</div>
                </div>
            ))}
        </div>
    );
}

// ── Photos Tab (Section 2) ────────────────────────────────────────────────────
function PhotosTab({ venueId, venueName, defaultImageId, onDefaultChanged }) {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [settingDefault, setSettingDefault] = useState(null);
    const [lightbox, setLightbox] = useState(null);
    const [showUpload, setShowUpload] = useState(false);
    const [removingDefault, setRemovingDefault] = useState(false);

    const loadPhotos = useCallback(async () => {
        if (!venueId) return;
        setLoading(true);
        try {
            const token = await getIdToken();
            const res = await axios.get(`${config.API_URL}/images`, {
                params: { entityType: 'venue', entityId: venueId },
                headers: { Authorization: token },
            });
            const imgs = res.data.images || [];
            setImages(imgs);
            setError('');
            if (defaultImageId) {
                const defaultImg = imgs.find(i => i.id === defaultImageId);
                if (defaultImg) onDefaultChanged(defaultImg.id, defaultImg.image_url);
            }
        } catch {
            setError('Failed to load photos.');
        } finally {
            setLoading(false);
        }
    }, [venueId, defaultImageId, onDefaultChanged]);

    useEffect(() => { loadPhotos(); }, [loadPhotos]);

    const handleUploaded = (img) => {
        setImages(prev => [img, ...prev]);
        setShowUpload(false);
    };

    const handleRemoveDefault = async () => {
        setRemovingDefault(true);
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: null, entityType: 'venue', entityId: venueId },
                { headers: { Authorization: token } }
            );
            onDefaultChanged(null, null);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to remove default image.');
        } finally {
            setRemovingDefault(false);
        }
    };

    const handleSetDefault = async (image) => {
        setSettingDefault(image.id);
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: image.id, entityType: 'venue', entityId: venueId },
                { headers: { Authorization: token } }
            );
            onDefaultChanged(image.id, image.image_url);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to set default image.');
        } finally {
            setSettingDefault(null);
        }
    };

    if (loading) return <div style={styles.emptyInfo}>Loading photos…</div>;
    if (error) return <div style={styles.emptyInfo}>{error}</div>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                    {defaultImageId && (
                        <button
                            onClick={handleRemoveDefault}
                            disabled={removingDefault}
                            style={{ background: 'transparent', border: '1px solid #555', borderRadius: '6px', color: '#aaa', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', opacity: removingDefault ? 0.6 : 1 }}
                        >
                            {removingDefault ? 'Removing…' : '✕ Remove Default'}
                        </button>
                    )}
                </div>
                <button onClick={() => setShowUpload(true)} style={styles.addPhotoBtn}>
                    + Add Photo
                </button>
            </div>

            {images.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>📷</div>
                    <div style={{ color: '#666', fontSize: '14px' }}>No photos yet for this venue.</div>
                </div>
            ) : (
                <div style={styles.photoGrid}>
                    {images.map(img => {
                        const isDefault = img.id === defaultImageId;
                        return (
                            <div key={img.id} style={{ ...styles.photoCard, ...(isDefault ? styles.photoCardDefault : {}) }}>
                                <div style={styles.photoThumb} onClick={() => setLightbox(img)}>
                                    <img src={img.image_url} alt={img.caption || venueName} style={styles.photoThumbImg} loading="lazy" />
                                    {isDefault && <span style={styles.defaultBadge}>★ Default</span>}
                                    {!img.is_public && <span style={styles.privateBadge}>🔒</span>}
                                </div>
                                <div style={styles.photoCardBody}>
                                    {img.caption && <div style={styles.photoCaption}>{img.caption}</div>}
                                    {!isDefault && img.is_public && (
                                        <button onClick={() => handleSetDefault(img)} disabled={settingDefault === img.id} style={styles.setDefaultBtn}>
                                            {settingDefault === img.id ? 'Setting…' : '★ Set as Default'}
                                        </button>
                                    )}
                                    {isDefault && <div style={styles.isDefaultLabel}>Current default photo</div>}
                                    {!img.is_public && <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>Private — only visible to you</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {lightbox && (
                <div style={styles.lightboxOverlay} onClick={() => setLightbox(null)}>
                    <div style={{ position: 'relative' }}>
                        <img src={lightbox.image_url} alt={lightbox.caption || ''} style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '8px' }} />
                        {lightbox.caption && <div style={styles.lightboxCaption}>{lightbox.caption}</div>}
                        <button onClick={() => setLightbox(null)} style={styles.lightboxClose}>✕</button>
                    </div>
                </div>
            )}

            {showUpload && (
                <UploadModal
                    onClose={() => setShowUpload(false)}
                    onUploaded={handleUploaded}
                    presetEntityType="venue"
                    presetEntityId={venueId}
                    presetEntityName={venueName}
                />
            )}
        </div>
    );
}

// ── Show History Tab ──────────────────────────────────────────────────────────
function ShowHistoryTab({ shows, attendedMap, performedMap, workedMap, onToggleAttended, togglingMap }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded at this venue yet.</div>;

    const q = search.toLowerCase();
    const filtered = search ? shows.filter(show => {
        const dateStr = show.event_date
            ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
            : '';
        return [dateStr, show.headliners, show.support_acts, show.stage_name, show.festival_name]
            .some(val => val && val.toLowerCase().includes(q));
    }) : shows;

    const totalShows = shows.length;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input
                type="text"
                placeholder="Search by date, artists, or stage..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No shows match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Show #</th>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Artists</th>
                                    <th style={styles.th}>Stage</th>
                                    <th style={styles.th}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((show, idx) => {
                                    const globalIdx = shows.indexOf(show);
                                    const showNum = totalShows - globalIdx;
                                    const dateObj = show.event_date ? new Date(show.event_date) : null;
                                    const formattedDate = dateObj
                                        ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                                        : '—';
                                    const isAttended = attendedMap[show.event_id] === true;
                                    const isPerformed = performedMap[show.event_id] === true;
                                    const isWorked = workedMap[show.event_id] === true;
                                    const isToggling = togglingMap[show.event_id];

                                    return (
                                        <tr key={show.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                <Link to={`/events/${show.event_slug || show.event_id}`} style={styles.tableLink}>{showNum}</Link>
                                            </td>
                                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{formattedDate}</td>
                                            <td style={{ ...styles.td, color: '#ccc', fontSize: '13px' }}>
                                                {(() => {
                                                    const allArtists = [show.headliners, show.support_acts].filter(Boolean).join(', ');
                                                    const titlePrefix = show.event_title ? `${show.event_title}: ` : '';
                                                    if (show.festival_name && show.festival_slug) {
                                                        return <>{titlePrefix}<Link to={`/festivals/${show.festival_slug}`} style={{ color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' }}>{show.festival_name}</Link>{allArtists ? `, ${allArtists}` : ''}</>;
                                                    }
                                                    const full = [titlePrefix + (allArtists || '')].join('').trim();
                                                    return full || '—';
                                                })()}
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                {show.stage_name || '—'}
                                            </td>
                                            <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {isPerformed ? (
                                                    <span style={styles.performedBadge}>Performed</span>
                                                ) : isWorked ? (
                                                    <span style={styles.workedBadge}>Worked</span>
                                                ) : (
                                                    <button
                                                        onClick={() => onToggleAttended(show.event_id)}
                                                        disabled={isToggling}
                                                        style={isAttended ? styles.attendedBtnOn : styles.attendedBtnOff}
                                                    >
                                                        {isToggling ? '…' : isAttended ? '✓ Attended' : 'Attended?'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Shows by Year Tab ─────────────────────────────────────────────────────────
function ShowsByYearTab({ shows }) {
    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded at this venue yet.</div>;

    const years = shows.filter(s => s.event_date).map(s => new Date(s.event_date).getUTCFullYear());
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    const counts = {};
    years.forEach(y => { counts[y] = (counts[y] || 0) + 1; });
    const data = [];
    for (let y = minYear; y <= maxYear; y++) data.push({ year: y, count: counts[y] || 0 });

    const maxCount = Math.max(...data.map(d => d.count), 1);
    const barMaxHeight = 200;
    const step = maxCount <= 5 ? 1 : maxCount <= 10 ? 2 : maxCount <= 20 ? 5 : 10;
    const gridLines = [];
    for (let v = 0; v <= maxCount; v += step) gridLines.push(v);
    if (gridLines[gridLines.length - 1] < maxCount) gridLines.push(maxCount);
    const chartWidth = Math.max(data.length * 52, 400);
    const yAxisWidth = 36;
    const chartPadTop = 16;
    const chartPadBottom = 40;
    const totalHeight = barMaxHeight + chartPadTop + chartPadBottom;

    return (
        <div>
            <div style={{ overflowX: 'auto', paddingBottom: '8px' }}>
                <div style={{ display: 'flex', minWidth: chartWidth + yAxisWidth + 'px' }}>
                    <div style={{ width: yAxisWidth + 'px', flexShrink: 0, position: 'relative', height: totalHeight + 'px' }}>
                        {gridLines.map(v => {
                            const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight;
                            return <div key={v} style={{ position: 'absolute', top: top + 'px', right: '6px', transform: 'translateY(-50%)', fontSize: '11px', color: '#666', whiteSpace: 'nowrap' }}>{v}</div>;
                        })}
                    </div>
                    <div style={{ flex: 1, position: 'relative', height: totalHeight + 'px' }}>
                        {gridLines.map(v => {
                            const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight;
                            return <div key={v} style={{ position: 'absolute', top: top + 'px', left: 0, right: 0, borderTop: `1px solid ${v === 0 ? '#444' : '#2a2a2a'}`, zIndex: 0 }} />;
                        })}
                        <div style={{ display: 'flex', alignItems: 'flex-end', height: barMaxHeight + 'px', marginTop: chartPadTop + 'px', gap: '4px', paddingLeft: '8px', paddingRight: '8px', position: 'relative', zIndex: 1 }}>
                            {data.map(({ year, count }) => {
                                const barH = count === 0 ? 0 : Math.max((count / maxCount) * barMaxHeight, 4);
                                return (
                                    <div key={year} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }} title={`${year}: ${count} show${count !== 1 ? 's' : ''}`}>
                                        {count > 0 && <div style={{ fontSize: '11px', color: '#bb86fc', fontWeight: 'bold', opacity: 0.85 }}>{count}</div>}
                                        <div style={{ width: '100%', maxWidth: '44px', height: barH + 'px', backgroundColor: count === 0 ? 'transparent' : '#bb86fc', borderRadius: '3px 3px 0 0' }} />
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', paddingLeft: '8px', paddingRight: '8px', gap: '4px', marginTop: '6px' }}>
                            {data.map(({ year, count }) => (
                                <div key={year} style={{ flex: 1, textAlign: 'center', fontSize: '11px', color: count === 0 ? '#444' : '#888', whiteSpace: 'nowrap', overflow: 'hidden' }}>{year}</div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '32px', fontSize: '13px', color: '#888' }}>
                <span>Total Years Active: <strong style={{ color: '#e0e0e0' }}>{data.filter(d => d.count > 0).length}</strong></span>
                <span>Peak Year: <strong style={{ color: '#bb86fc' }}>{data.reduce((best, d) => d.count > best.count ? d : best, data[0]).year} ({data.reduce((best, d) => d.count > best.count ? d : best, data[0]).count} shows)</strong></span>
            </div>
        </div>
    );
}

// ── Artists Hosted Tab ────────────────────────────────────────────────────────
function ArtistsHostedTab({ artists }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!artists || artists.length === 0) return <div style={styles.emptyTab}>No artist data recorded yet.</div>;

    const q = search.toLowerCase();
    const filtered = search ? artists.filter(a =>
        [a.name, a.artist_type, a.artist_style, a.city, a.state, a.country]
            .some(val => val && val.toLowerCase().includes(q))
    ) : artists;

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input
                type="text"
                placeholder="Search by artist, type, style, city, state, country..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No artists match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Artist</th>
                                    <th style={styles.th}>Artist Type</th>
                                    <th style={styles.th}>Artist Style</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State/Region</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Appearances</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((artist, idx) => (
                                    <tr key={artist.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                        <td style={styles.td}>
                                            <Link to={`/artists/${artist.slug || artist.id}`} style={styles.tableLink}>{artist.name}</Link>
                                        </td>
                                        <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{artist.artist_type || '—'}</td>
                                        <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{artist.artist_style || '—'}</td>
                                        <td style={styles.td}>{artist.city || '—'}</td>
                                        <td style={styles.td}>{artist.state || '—'}</td>
                                        <td style={styles.td}>{artist.country || '—'}</td>
                                        <td style={styles.td}>
                                            <span style={styles.showCount}>{artist.appearances}x</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Promoters Hosted Tab ──────────────────────────────────────────────────────
function PromotersHostedTab({ promoters }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!promoters || promoters.length === 0) return <div style={styles.emptyTab}>No promoter data recorded yet.</div>;

    const q = search.toLowerCase();
    const filtered = search ? promoters.filter(p =>
        [p.name, p.city, p.state, p.country]
            .some(val => val && val.toLowerCase().includes(q))
    ) : promoters;

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input
                type="text"
                placeholder="Search by promoter, city, state, country..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No promoters match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Promoter</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State/Region</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}># of Shows</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((p, idx) => (
                                    <tr key={p.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                        <td style={styles.td}>
                                            <Link to={`/promoters/${p.slug || p.id}`} style={styles.tableLink}>{p.name}</Link>
                                        </td>
                                        <td style={styles.td}>{p.city || '—'}</td>
                                        <td style={styles.td}>{p.state || '—'}</td>
                                        <td style={styles.td}>{p.country || '—'}</td>
                                        <td style={styles.td}>
                                            <span style={styles.showCount}>{p.show_count}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Festivals Hosted Tab ──────────────────────────────────────────────────────
function FestivalsHostedTab({ festivals, festivalEvents, formatDate }) {
    const [expandedFestival, setExpandedFestival] = useState(null);
    const [search, setSearch] = useState('');

    if (!festivals || festivals.length === 0) return <div style={styles.emptyTab}>No festivals recorded at this venue yet.</div>;

    const q = search.toLowerCase();
    const filtered = search
        ? festivals.filter(f =>
            [f.festival_name, f.headliners].some(val => val && val.toLowerCase().includes(q))
          )
        : festivals;

    return (
        <div>
            <input
                type="text"
                placeholder="Search by festival name or headliner..."
                value={search}
                onChange={e => { setSearch(e.target.value); setExpandedFestival(null); }}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No festivals match your search.</div>
            ) : (
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}></th>
                                <th style={styles.th}>Festival</th>
                                <th style={styles.th}>Start Date</th>
                                <th style={styles.th}>End Date</th>
                                <th style={styles.th}>Headliners</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((f, idx) => {
                                const isExpanded = expandedFestival === f.festival_id;
                                const festEvents = (festivalEvents || []).filter(e => e.festival_id === f.festival_id);
                                return (
                                    <React.Fragment key={f.festival_id}>
                                        <tr
                                            style={{ ...(idx % 2 === 0 ? styles.trEven : styles.trOdd), cursor: festEvents.length > 0 ? 'pointer' : 'default' }}
                                            onClick={() => festEvents.length > 0 && setExpandedFestival(isExpanded ? null : f.festival_id)}
                                        >
                                            <td style={{ ...styles.td, width: '28px', color: '#bb86fc', fontSize: '12px' }}>
                                                {festEvents.length > 0 ? (isExpanded ? '▾' : '▸') : ''}
                                            </td>
                                            <td style={styles.td}>
                                                <Link to={`/festivals/${f.festival_slug || f.festival_id}`} style={styles.tableLink} onClick={e => e.stopPropagation()}>
                                                    {f.festival_name}
                                                </Link>
                                            </td>
                                            <td style={styles.td}>{formatDate(f.start_date)}</td>
                                            <td style={styles.td}>{formatDate(f.end_date)}</td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{f.headliners || '—'}</td>
                                        </tr>
                                        {isExpanded && (
                                            <tr>
                                                <td colSpan={5} style={{ padding: '0', backgroundColor: '#131313', borderBottom: '1px solid #333' }}>
                                                    <table style={{ ...styles.table, margin: 0 }}>
                                                        <thead>
                                                            <tr style={{ backgroundColor: '#1a1a1a' }}>
                                                                <th style={{ ...styles.th, paddingLeft: '40px' }}>Date</th>
                                                                <th style={styles.th}>Headliners</th>
                                                                <th style={styles.th}>Support</th>
                                                                <th style={styles.th}>Promoter(s)</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {festEvents.map((e, ei) => {
                                                                const formattedDate = e.event_date
                                                                    ? new Date(e.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                                                                    : '—';
                                                                return (
                                                                    <tr key={e.event_id} style={{ backgroundColor: ei % 2 === 0 ? '#161616' : '#131313' }}>
                                                                        <td style={{ ...styles.td, paddingLeft: '40px' }}>
                                                                            <Link to={`/events/${e.event_slug || e.event_id}`} style={styles.tableLink}>
                                                                                {formattedDate}
                                                                            </Link>
                                                                        </td>
                                                                        <td style={styles.td}>{e.headliners || '—'}</td>
                                                                        <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{e.support_acts || '—'}</td>
                                                                        <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                                            {e.promoters && e.promoters.length > 0
                                                                                ? e.promoters.map((p, pi) => (
                                                                                    <React.Fragment key={pi}>
                                                                                        {pi > 0 && ', '}
                                                                                        <Link to={`/promoters/${p.slug || p.name}`} style={styles.tableLink}>{p.name}</Link>
                                                                                    </React.Fragment>
                                                                                ))
                                                                                : '—'}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ── Stages Tab ────────────────────────────────────────────────────────────────
function StagesTab({ stages, venueId, onStagesUpdated }) {
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const emptyForm = { name: '', capacity: '', description: '', is_default: false };
    const [form, setForm] = useState(emptyForm);

    if (!stages) return <div style={styles.emptyTab}>Loading stages...</div>;

    const handleAdd = () => { setForm(emptyForm); setEditingId(null); setShowAddForm(true); setDeleteError(''); };
    const handleEdit = (stage) => {
        setForm({ name: stage.name || '', capacity: stage.capacity != null ? String(stage.capacity) : '', description: stage.description || '', is_default: stage.is_default || false });
        setEditingId(stage.id); setShowAddForm(false); setDeleteError('');
    };
    const handleCancel = () => { setShowAddForm(false); setEditingId(null); setForm(emptyForm); setDeleteError(''); };

    const handleSaveNew = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            await axios.post(`${config.API_URL}/venues/${venueId}/stages`, { name: form.name.trim(), capacity: form.capacity ? parseInt(form.capacity) : null, description: form.description.trim() || null, is_default: form.is_default });
            setShowAddForm(false); setForm(emptyForm); onStagesUpdated();
        } catch (err) { console.error('Failed to create stage', err); }
        setSaving(false);
    };

    const handleSaveEdit = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            await axios.put(`${config.API_URL}/stages/${editingId}`, { name: form.name.trim(), capacity: form.capacity ? parseInt(form.capacity) : null, description: form.description.trim() || null, is_default: form.is_default });
            setEditingId(null); setForm(emptyForm); onStagesUpdated();
        } catch (err) { console.error('Failed to update stage', err); }
        setSaving(false);
    };

    const handleDelete = async (stage) => {
        if (stage.is_default) { setDeleteError(`"${stage.name}" is the default stage and cannot be deleted. Set another stage as default first.`); return; }
        if (!window.confirm(`Delete stage "${stage.name}"? Any events linked to this stage will be unlinked.`)) return;
        setDeleteError('');
        try { await axios.delete(`${config.API_URL}/stages/${stage.id}`); onStagesUpdated(); }
        catch (err) { setDeleteError('Failed to delete stage.'); }
    };

    return (
        <div>
            {deleteError && <div style={styles.stageError}>{deleteError}</div>}
            {stages.length === 0 ? (
                <div style={styles.emptyTab}>No stages recorded yet.</div>
            ) : (
                <div style={styles.stageList}>
                    {stages.map(stage => (
                        <div key={stage.id} style={styles.stageCard}>
                            {editingId === stage.id ? (
                                <StageForm form={form} setForm={setForm} onSave={handleSaveEdit} onCancel={handleCancel} saving={saving} />
                            ) : (
                                <div style={styles.stageCardInner}>
                                    <div style={styles.stageCardLeft}>
                                        <div style={styles.stageName}>
                                            {stage.name}
                                            {stage.is_default && <span style={styles.stageBadge}>Default</span>}
                                        </div>
                                        {stage.capacity && <div style={styles.stageMeta}>Capacity: {stage.capacity.toLocaleString()}</div>}
                                        {stage.description && <div style={styles.stageDesc}>{stage.description}</div>}
                                    </div>
                                    <div style={styles.stageCardActions}>
                                        <button onClick={() => handleEdit(stage)} style={styles.stageEditBtn}>Edit</button>
                                        <button onClick={() => handleDelete(stage)} style={styles.stageDeleteBtn}>Delete</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {showAddForm && (
                <div style={{ ...styles.stageCard, marginTop: '16px' }}>
                    <div style={styles.stageFormTitle}>New Stage</div>
                    <StageForm form={form} setForm={setForm} onSave={handleSaveNew} onCancel={handleCancel} saving={saving} />
                </div>
            )}
            {!showAddForm && <button onClick={handleAdd} style={styles.addStageBtn}>+ Add Stage</button>}
        </div>
    );
}

function StageForm({ form, setForm, onSave, onCancel, saving }) {
    return (
        <div>
            <div style={styles.stageFormRow}>
                <label style={styles.stageFormLabel}>Stage Name *</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Main Stage, Tent Stage" style={styles.stageInput} />
            </div>
            <div style={styles.stageFormRow}>
                <label style={styles.stageFormLabel}>Capacity</label>
                <input type="number" value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} placeholder="Stage capacity" style={{ ...styles.stageInput, width: '180px' }} />
            </div>
            <div style={styles.stageFormRow}>
                <label style={styles.stageFormLabel}>Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional notes about this stage" rows={2} style={{ ...styles.stageInput, resize: 'vertical' }} />
            </div>
            <div style={{ ...styles.stageFormRow, alignItems: 'center', gap: '10px', flexDirection: 'row' }}>
                <input type="checkbox" id="is_default_stage" checked={form.is_default} onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))} style={{ accentColor: '#bb86fc', width: '16px', height: '16px', cursor: 'pointer' }} />
                <label htmlFor="is_default_stage" style={{ ...styles.stageFormLabel, margin: 0, cursor: 'pointer' }}>Set as default stage</label>
            </div>
            <div style={styles.stageFormActions}>
                <button onClick={onSave} disabled={saving || !form.name.trim()} style={styles.stageSaveBtn}>{saving ? 'Saving...' : 'Save'}</button>
                <button onClick={onCancel} style={styles.stageCancelBtn}>Cancel</button>
            </div>
        </div>
    );
}

// ── Flyer Card ────────────────────────────────────────────────────────────────
function FlyerCard({ show }) {
    const [hovered, setHovered] = useState(false);
    const [imgError, setImgError] = useState(false);
    const formattedDate = show.event_date
        ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
        : '';
    const tooltip = [formattedDate, show.headliners, show.stage_name].filter(Boolean).join(' · ');
    return (
        <Link
            to={`/events/${show.event_slug || show.event_id}`}
            style={styles.flyerCell}
            title={tooltip}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {imgError ? (
                <div style={styles.flyerErrorBox}>
                    <div style={styles.flyerErrorIcon}>🎟️</div>
                    <div style={styles.flyerErrorDate}>{formattedDate}</div>
                    <div style={styles.flyerErrorVenue}>{show.headliners || ''}</div>
                </div>
            ) : (
                <img
                    src={show.flyer_url}
                    alt={tooltip}
                    style={{ ...styles.flyerImg, opacity: hovered ? 0.85 : 1 }}
                    loading="lazy"
                    onError={() => setImgError(true)}
                />
            )}
            {!imgError && (
                <div style={{ ...styles.flyerOverlay, opacity: hovered ? 1 : 0 }}>
                    <div style={styles.flyerDate}>{formattedDate}</div>
                </div>
            )}
        </Link>
    );
}

// ── Flyer History Tab ─────────────────────────────────────────────────────────
function FlyerHistoryTab({ shows }) {
    const [page, setPage] = useState(1);
    const [sortKey, setSortKey] = useState('date_desc');
    const [filterType, setFilterType] = useState('artist');
    const [filterSearch, setFilterSearch] = useState('');

    const flyers = shows ? shows.filter(s => s.flyer_url) : [];

    if (flyers.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>🎟️</div>
                <div style={{ color: '#888', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>No Flyers Yet</div>
                <div style={{ color: '#555', fontSize: '14px' }}>Event flyers will appear here as they are added.</div>
            </div>
        );
    }

    const q = filterSearch.trim().toLowerCase();
    const filtered = q ? flyers.filter(s => {
        if (filterType === 'artist') return (s.headliners && s.headliners.toLowerCase().includes(q)) || (s.support_acts && s.support_acts.toLowerCase().includes(q));
        if (filterType === 'festival') return s.festival_name && s.festival_name.toLowerCase().includes(q);
        if (filterType === 'promoter') return s.promoters && s.promoters.toLowerCase().includes(q);
        return true;
    }) : flyers;

    const sorted = [...filtered].sort((a, b) => {
        if (sortKey === 'date_desc') return new Date(b.event_date) - new Date(a.event_date);
        if (sortKey === 'date_asc') return new Date(a.event_date) - new Date(b.event_date);
        if (sortKey === 'att_desc') {
            const aVal = a.estimated_attendance != null ? parseInt(a.estimated_attendance) : -1;
            const bVal = b.estimated_attendance != null ? parseInt(b.estimated_attendance) : -1;
            return bVal - aVal;
        }
        if (sortKey === 'att_asc') {
            const aVal = a.estimated_attendance != null ? parseInt(a.estimated_attendance) : Infinity;
            const bVal = b.estimated_attendance != null ? parseInt(b.estimated_attendance) : Infinity;
            return aVal - bVal;
        }
        return 0;
    });

    const totalPages = Math.ceil(sorted.length / FLYER_PAGE_SIZE);
    const paginated = sorted.slice((page - 1) * FLYER_PAGE_SIZE, page * FLYER_PAGE_SIZE);
    const remainder = paginated.length % FLYER_COLS;
    const padCount = remainder === 0 ? 0 : FLYER_COLS - remainder;

    const filterPlaceholder = filterType === 'artist' ? 'Search artist...' : filterType === 'festival' ? 'Search festival...' : 'Search promoter...';

    return (
        <div>
            <div style={styles.flyerControlsBar}>
                <div style={styles.flyerFilterGroup}>
                    <select value={filterType} onChange={e => { setFilterType(e.target.value); setFilterSearch(''); setPage(1); }} style={styles.flyerFilterTypeSelect}>
                        <option value="artist">Artist</option>
                        <option value="festival">Festival</option>
                        <option value="promoter">Promoter</option>
                    </select>
                    <input type="text" value={filterSearch} onChange={e => { setFilterSearch(e.target.value); setPage(1); }} placeholder={filterPlaceholder} style={styles.flyerFilterInput} />
                    {filterSearch && (
                        <button onClick={() => { setFilterSearch(''); setPage(1); }} style={styles.flyerFilterClear} title="Clear filter">✕</button>
                    )}
                </div>
                <div style={styles.flyerSortGroup}>
                    <label style={styles.flyerSortLabel}>Sort by</label>
                    <select value={sortKey} onChange={e => { setSortKey(e.target.value); setPage(1); }} style={styles.flyerSortSelect}>
                        {FLYER_SORT_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {sorted.length === 0 ? (
                <div style={styles.emptyTab}>No flyers match your filter.</div>
            ) : (
                <>
                    <div style={styles.flyerGrid}>
                        {paginated.map((show) => (
                            <FlyerCard key={show.event_id} show={show} />
                        ))}
                        {Array.from({ length: padCount }).map((_, i) => (
                            <div key={`pad-${i}`} style={styles.flyerCellPad} />
                        ))}
                    </div>
                    {totalPages > 1 && (
                        <FlyerPagination currentPage={page} totalItems={sorted.length} onPageChange={setPage} />
                    )}
                    <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', color: '#555' }}>
                        {q ? `${sorted.length} of ${flyers.length} flyer${flyers.length !== 1 ? 's' : ''}` : `${flyers.length} flyer${flyers.length !== 1 ? 's' : ''}`}
                        {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ''}
                    </div>
                </>
            )}
        </div>
    );
}

// ── Shows Without Flyers Tab ──────────────────────────────────────────────────
function ShowsWithoutFlyersTab({ shows }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    const noFlyer = shows ? shows.filter(s => !s.flyer_url) : [];

    if (noFlyer.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>🎉</div>
                <div style={{ color: '#888', fontSize: '15px' }}>All shows have a flyer — nothing missing here.</div>
            </div>
        );
    }

    const q = search.toLowerCase();
    const filtered = search ? noFlyer.filter(s => {
        const dateStr = s.event_date ? new Date(s.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '';
        return [dateStr, s.headliners, s.support_acts, s.stage_name, s.festival_name].some(v => v && v.toLowerCase().includes(q));
    }) : noFlyer;

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input
                type="text"
                placeholder="Search by date, artists, stage, festival..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No shows match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Stage</th>
                                    <th style={styles.th}>Lineup</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((s, idx) => {
                                    const formattedDate = s.event_date
                                        ? new Date(s.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                                        : '—';
                                    const lineup = s.festival_name && s.festival_slug
                                        ? <><Link to={`/festivals/${s.festival_slug}`} style={styles.tableLink}>{s.festival_name}</Link>{s.headliners ? ` — ${s.headliners}` : ''}</>
                                        : s.headliners || s.support_acts
                                            ? <>{s.headliners && <span>{s.headliners}</span>}{s.support_acts && <span style={{ color: '#666', fontSize: '12px' }}>{s.headliners ? ' · ' : ''}{s.support_acts}</span>}</>
                                            : '—';
                                    return (
                                        <tr key={s.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                                                <Link to={`/events/${s.event_slug || s.event_id}`} style={styles.tableLink}>{formattedDate}</Link>
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{s.stage_name || '—'}</td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{lineup}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#555', textAlign: 'right' }}>
                        {filtered.length} show{filtered.length !== 1 ? 's' : ''} without a flyer
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
function VenueDetail({ slugOverride } = {}) {
    const params = useParams();
    const slug = slugOverride || params.slug;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeInfoTab, setActiveInfoTab] = useState(0);
    const [activeParentTab, setActiveParentTab] = useState(0);
    const [activeFlyersSubTab, setActiveFlyersSubTab] = useState(0);

    const [defaultImageId, setDefaultImageId] = useState(null);
    const [defaultImageUrl, setDefaultImageUrl] = useState(null);

    const [attendedMap, setAttendedMap] = useState({});
    const [performedMap, setPerformedMap] = useState({});
    const [workedMap, setWorkedMap] = useState({});
    const [togglingMap, setTogglingMap] = useState({});

    const loadAttendedStatus = useCallback(async (shows) => {
        if (!shows || shows.length === 0) return;
        try {
            const eventIds = shows.map(s => s.event_id);
            const statusMap = await fetchBatchEventStatus(eventIds);
            const aMap = {}; const pMap = {}; const wMap = {};
            Object.entries(statusMap).forEach(([id, status]) => {
                aMap[id] = status.attended; pMap[id] = status.performed || false; wMap[id] = status.worked || false;
            });
            setAttendedMap(aMap); setPerformedMap(pMap); setWorkedMap(wMap);
        } catch { /* not logged in */ }
    }, []);

    const handleToggleAttended = useCallback(async (eventId) => {
        setTogglingMap(prev => ({ ...prev, [eventId]: true }));
        try {
            const result = await toggleAttended(eventId);
            setAttendedMap(prev => ({ ...prev, [eventId]: result.attended }));
            setPerformedMap(prev => ({ ...prev, [eventId]: result.performed || false }));
            setWorkedMap(prev => ({ ...prev, [eventId]: result.worked || false }));
        } catch (e) { console.error('toggleAttended error:', e); }
        finally { setTogglingMap(prev => ({ ...prev, [eventId]: false })); }
    }, []);

    const fetchVenue = async () => {
        try {
            const res = await axios.get(`${config.API_URL}/venues/${slug}`);
            setData(res.data);
            setLoading(false);
            setDefaultImageId(null);
            setDefaultImageUrl(null);
            if (res.data?.venue?.default_image_id) {
                setDefaultImageId(res.data.venue.default_image_id);
                // If default_image_url were added to Lambda we'd use it here; for now resolve via images API when Photos tab is opened
            }
            if (res.data?.venue) document.title = `SHARC - ${res.data.venue.name}`;
            if (res.data?.show_history) loadAttendedStatus(res.data.show_history);
        } catch {
            setError('Failed to load venue');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVenue();
        return () => { document.title = 'SHARC - Shows Archive'; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const handleDefaultChanged = useCallback((imageId, imageUrl) => {
        setDefaultImageId(imageId);
        setDefaultImageUrl(imageUrl);
    }, []);

    if (loading) return <div style={styles.message}>Loading venue...</div>;
    if (error) return <div style={styles.error}>{error}</div>;
    if (!data) return null;

    const { venue, stats, links, stages, show_history, artists_hosted, promoters_hosted, festivals_hosted, festival_events, projects } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
    };

    const heroImage = defaultImageUrl || venue.image_url;

    // Compute term of shows
    let termStr = '—';
    if (stats.first_show_date && stats.last_show_date) {
        let d1 = new Date(stats.first_show_date); let d2 = new Date(stats.last_show_date);
        let years = d2.getUTCFullYear() - d1.getUTCFullYear();
        let months = d2.getUTCMonth() - d1.getUTCMonth();
        let days = d2.getUTCDate() - d1.getUTCDate();
        if (days < 0) { months -= 1; days += new Date(d2.getUTCFullYear(), d2.getUTCMonth(), 0).getDate(); }
        if (months < 0) { years -= 1; months += 12; }
        const parts = [];
        if (years > 0) parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
        if (months > 0) parts.push(`${months} ${months === 1 ? 'Month' : 'Months'}`);
        if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
        termStr = parts.join(' ') || '—';
    }

    return (
        <div>
            {/* ── Section 1: Venue Name + Edit button ── */}
            <div style={styles.section1}>
                <h1 style={styles.venueNameHeading}>{venue.name}</h1>
                <Link to={`/venues/${venue.slug || venue.id}/edit`} style={styles.editButton}>Edit Venue</Link>
            </div>

            {/* ── Section 2: Image/meta left + info tabs right ── */}
            <div style={styles.headerGrid}>
                {/* Left: image + location + type/capacity */}
                <div style={styles.headerLeft}>
                    <div style={styles.headerImageRow}>
                        {heroImage ? (
                            <img
                                src={heroImage}
                                alt={venue.name}
                                style={styles.venueImage}
                                onError={e => { e.target.outerHTML = `<div style="width:160px;height:160px;border-radius:8px;border:1px solid #333;background:#1e1e1e;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;flex-shrink:0"><div style="font-size:32px;opacity:0.3">🏛️</div><div style="font-size:11px;color:#555;text-transform:uppercase;letter-spacing:0.5px">No Image</div></div>`; }}
                            />
                        ) : (
                            <div style={styles.noImageBox}>
                                <div style={styles.noImageIcon}>🏛️</div>
                                <div style={styles.noImageLabel}>No Image</div>
                            </div>
                        )}
                        <div style={styles.headerMeta}>
                            {[venue.city, venue.state, venue.country].filter(Boolean).length > 0 && (
                                <div style={styles.location}>{[venue.city, venue.state, venue.country].filter(Boolean).join(', ')}</div>
                            )}
                            {venue.venue_type && (
                                <span style={styles.tag}>{venue.venue_type}</span>
                            )}
                            {venue.capacity && (
                                <span style={{ ...styles.tag, marginTop: '4px' }}>Capacity: {venue.capacity.toLocaleString()}</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: info tabs */}
                <div style={styles.headerRight}>
                    <div style={styles.infoTabBar}>
                        {INFO_TABS.map((tab, i) => (
                            <button
                                key={tab}
                                onClick={() => setActiveInfoTab(i)}
                                style={{ ...styles.infoTabBtn, ...(activeInfoTab === i ? styles.infoTabBtnActive : {}) }}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                    <div style={styles.infoTabContent}>
                        {/* Show Stats */}
                        {activeInfoTab === 0 && (
                            <div style={styles.showStatsLayout}>
                                <div style={styles.totalShowsBox}>
                                    <div style={styles.statValue}>{stats.total_shows || 0}</div>
                                    <div style={styles.statLabel}>Total Shows</div>
                                </div>
                                <table style={styles.statsTable}>
                                    <tbody>
                                        <tr>
                                            <td style={styles.statsTableLabel}>First Show</td>
                                            <td style={styles.statsTableValue}>{formatDate(stats.first_show_date)}</td>
                                        </tr>
                                        <tr>
                                            <td style={styles.statsTableLabel}>Most Recent Show</td>
                                            <td style={styles.statsTableValue}>{formatDate(stats.last_show_date)}</td>
                                        </tr>
                                        <tr>
                                            <td style={styles.statsTableLabel}>Term of Shows</td>
                                            <td style={styles.statsTableValue}>{termStr}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Bio */}
                        {activeInfoTab === 1 && (
                            <div>
                                {venue.description
                                    ? <div style={styles.bioText}>{venue.description}</div>
                                    : <div style={styles.emptyInfo}>No bio available.</div>}
                            </div>
                        )}

                        {/* Links */}
                        {activeInfoTab === 2 && (
                            <div>
                                {links && links.length > 0
                                    ? <div style={styles.linksGrid}>
                                        {links.map(link => (
                                            <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" style={styles.linkChip}>
                                                {link.description} ↗
                                            </a>
                                        ))}
                                    </div>
                                    : <div style={styles.emptyInfo}>No links added yet.</div>}
                            </div>
                        )}

                        {/* Photos */}
                        {activeInfoTab === 3 && (
                            <PhotosTab
                                venueId={venue.id}
                                venueName={venue.name}
                                defaultImageId={defaultImageId}
                                onDefaultChanged={handleDefaultChanged}
                            />
                        )}

                        {/* Projects */}
                        {activeInfoTab === 4 && (
                            <ProjectsTab projects={projects} />
                        )}

                        {/* Additional Info */}
                        {activeInfoTab === 5 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {[venue.street, venue.city, venue.state, venue.country].filter(Boolean).length > 0 && (
                                    <div>
                                        <div style={styles.infoLabel}>Address</div>
                                        <div style={styles.infoValue}>
                                            {[venue.street, venue.city, venue.state, venue.country].filter(Boolean).join(', ')}
                                            {venue.postal_code ? ` ${venue.postal_code}` : ''}
                                        </div>
                                    </div>
                                )}
                                {venue.status && (
                                    <div>
                                        <div style={styles.infoLabel}>Venue Status</div>
                                        <div style={{ ...styles.infoValue, color: venue.status === 'Open' ? '#03dac6' : venue.status === 'Closed' ? '#cf6679' : '#ffb74d' }}>
                                            {venue.status}
                                        </div>
                                    </div>
                                )}
                                {venue.age_limit && (
                                    <div>
                                        <div style={styles.infoLabel}>Age Restriction</div>
                                        <div style={styles.infoValue}>{venue.age_limit}</div>
                                    </div>
                                )}
                                {!venue.street && !venue.status && !venue.age_limit && (
                                    <div style={styles.emptyInfo}>No additional information available.</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Section 3: Parent tabs ── */}
            <div style={styles.tabsWrapper}>
                <div style={styles.tabBar}>
                    {PARENT_TABS.map((tab, i) => (
                        <button key={tab} onClick={() => setActiveParentTab(i)} style={{ ...styles.tabBtn, ...(activeParentTab === i ? styles.tabBtnActive : {}) }}>{tab}</button>
                    ))}
                </div>
                <div style={styles.tabContent}>
                    {activeParentTab === 0 && (
                        <ShowHistoryTab
                            shows={show_history}
                            attendedMap={attendedMap}
                            performedMap={performedMap}
                            workedMap={workedMap}
                            onToggleAttended={handleToggleAttended}
                            togglingMap={togglingMap}
                        />
                    )}
                    {activeParentTab === 1 && <ShowsByYearTab shows={show_history} />}
                    {activeParentTab === 2 && (
                        <StagesTab stages={stages} venueId={venue.id} onStagesUpdated={fetchVenue} />
                    )}
                    {activeParentTab === 3 && <PromotersHostedTab promoters={promoters_hosted} />}
                    {activeParentTab === 4 && (
                        <FestivalsHostedTab
                            festivals={festivals_hosted}
                            festivalEvents={festival_events}
                            formatDate={formatDate}
                        />
                    )}
                    {activeParentTab === 5 && <ArtistsHostedTab artists={artists_hosted} />}
                    {activeParentTab === 6 && (
                        <div>
                            <SubTabBar tabs={FLYERS_SUBTABS} active={activeFlyersSubTab} onChange={setActiveFlyersSubTab} />
                            <div style={styles.subTabContent}>
                                {activeFlyersSubTab === 0 && <FlyerHistoryTab shows={show_history} />}
                                {activeFlyersSubTab === 1 && <ShowsWithoutFlyersTab shows={show_history} />}
                            </div>
                        </div>
                    )}
                    {activeParentTab === 7 && (
                        <div>
                            <div style={styles.analyticsGrid}>
                                <div style={styles.analyticsCard}>
                                    <div style={styles.analyticsValue}>{artists_hosted ? artists_hosted.length : 0}</div>
                                    <div style={styles.analyticsLabel}>Artist Performances</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    // ── Section 1 ──
    section1: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '8px' },
    venueNameHeading: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: 0, lineHeight: 1.2 },
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px' },
    editButton: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '6px 16px', fontSize: '14px', textDecoration: 'none', fontWeight: 'bold', flexShrink: 0 },

    // ── Section 2 header grid ──
    headerGrid: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px', marginBottom: '32px', alignItems: 'start' },
    headerLeft: { display: 'flex', flexDirection: 'column', gap: '12px' },
    headerImageRow: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
    venueImage: { width: '160px', height: '160px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #333', flexShrink: 0 },
    noImageBox: { width: '160px', height: '160px', borderRadius: '8px', border: '1px solid #333', background: '#1e1e1e', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', flexShrink: 0 },
    noImageIcon: { fontSize: '32px', opacity: 0.3 },
    noImageLabel: { fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' },
    headerMeta: { display: 'flex', flexDirection: 'column', gap: '4px', paddingTop: '4px' },
    location: { fontSize: '15px', color: '#aaa', marginBottom: '6px' },
    tag: { display: 'inline-block', backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '4px 10px', fontSize: '13px', color: '#bb86fc' },

    // ── Info tabs (right side of section 2) ──
    headerRight: {},
    infoTabBar: { display: 'flex', borderBottom: '2px solid #333', gap: '2px', overflowX: 'auto' },
    infoTabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-2px', padding: '10px 16px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap' },
    infoTabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    infoTabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '16px', minHeight: '160px', maxHeight: '280px', overflowY: 'auto' },
    bioText: { fontSize: '14px', color: '#ccc', lineHeight: '1.7', maxHeight: '96px', overflowY: 'auto' },

    // ── Show Stats tab ──
    showStatsLayout: { display: 'flex', gap: '24px', alignItems: 'center', height: '100%' },
    totalShowsBox: { flexShrink: 0, backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px 28px', textAlign: 'center', minWidth: '110px' },
    statsTable: { flex: 1, borderCollapse: 'collapse', fontSize: '14px' },
    statsTableLabel: { padding: '10px 16px 10px 0', color: '#aaa', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2a2a2a', whiteSpace: 'nowrap', width: '1%' },
    statsTableValue: { padding: '10px 0', color: '#e0e0e0', fontWeight: 'bold', fontSize: '15px', borderBottom: '1px solid #2a2a2a' },
    statValue: { fontSize: '36px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    statLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },

    // ── Links / info ──
    linksGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    linkChip: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 14px', fontSize: '13px', color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    emptyInfo: { color: '#555', fontSize: '14px', padding: '20px 0', textAlign: 'center' },
    infoLabel: { fontSize: '11px', color: '#777', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' },
    infoValue: { fontSize: '14px', color: '#ccc' },

    // ── Photos ──
    photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' },
    photoCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', overflow: 'hidden' },
    photoCardDefault: { borderColor: '#bb86fc' },
    photoThumb: { position: 'relative', width: '100%', paddingTop: '75%', overflow: 'hidden', cursor: 'zoom-in', background: '#111' },
    photoThumbImg: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' },
    defaultBadge: { position: 'absolute', top: '6px', left: '6px', background: '#bb86fc', color: '#121212', fontSize: '10px', fontWeight: 'bold', borderRadius: '4px', padding: '2px 6px' },
    privateBadge: { position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', borderRadius: '4px', padding: '2px 4px', fontSize: '12px' },
    photoCardBody: { padding: '8px' },
    photoCaption: { fontSize: '11px', color: '#aaa', marginBottom: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    setDefaultBtn: { width: '100%', background: 'transparent', border: '1px solid #bb86fc', borderRadius: '4px', color: '#bb86fc', fontSize: '11px', padding: '4px 6px', cursor: 'pointer' },
    isDefaultLabel: { fontSize: '11px', color: '#bb86fc', textAlign: 'center', padding: '4px 0' },
    addPhotoBtn: { background: '#bb86fc', color: '#121212', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' },
    lightboxOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 },
    lightboxCaption: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.7)', color: '#e0e0e0', padding: '8px 12px', fontSize: '13px', textAlign: 'center', borderRadius: '0 0 8px 8px' },
    lightboxClose: { position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', borderRadius: '4px', padding: '2px 8px' },

    // ── Section 3: Parent tabs ──
    tabsWrapper: { marginTop: '8px' },
    tabBar: { display: 'flex', borderBottom: '2px solid #333', marginBottom: '0', overflowX: 'auto', gap: '2px' },
    tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-2px', padding: '12px 20px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.3px' },
    tabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    tabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '24px', minHeight: '200px' },

    // ── Sub-tabs ──
    subTabBar: { display: 'flex', borderBottom: '1px solid #2e2e2e', gap: '2px', marginBottom: '0', overflowX: 'auto' },
    subTabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-1px', padding: '8px 16px', fontSize: '13px', fontWeight: '500', color: '#666', cursor: 'pointer', whiteSpace: 'nowrap' },
    subTabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    subTabContent: { paddingTop: '20px' },

    // ── Tables ──
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '11px 16px', color: '#e0e0e0', borderBottom: '1px solid #222' },
    trEven: { backgroundColor: '#1e1e1e' },
    trOdd: { backgroundColor: '#1a1a1a' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    showCount: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', color: '#bb86fc', fontWeight: 'bold' },
    attendedBtnOff: { backgroundColor: 'transparent', color: '#666', border: '1px solid #444', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
    attendedBtnOn: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' },
    performedBadge: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    workedBadge: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    pagination: { display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '20px', flexWrap: 'wrap' },
    pageBtn: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 12px', fontSize: '13px', color: '#aaa', cursor: 'pointer' },
    pageBtnActive: { backgroundColor: '#bb86fc', borderColor: '#bb86fc', color: '#121212', fontWeight: 'bold' },
    pageBtnDisabled: { opacity: 0.35, cursor: 'default' },
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    emptyTab: { textAlign: 'center', color: '#666', padding: '40px 0', fontSize: '15px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    error: { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },

    // ── Stages ──
    stageList: { display: 'flex', flexDirection: 'column', gap: '12px' },
    stageCard: { backgroundColor: '#222', border: '1px solid #333', borderRadius: '8px', padding: '16px' },
    stageCardInner: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' },
    stageCardLeft: { flex: 1 },
    stageName: { fontSize: '16px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '10px' },
    stageBadge: { backgroundColor: '#2c2c2c', border: '1px solid #bb86fc', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', color: '#bb86fc', fontWeight: 'bold', letterSpacing: '0.5px' },
    stageMeta: { fontSize: '13px', color: '#aaa', marginBottom: '4px' },
    stageDesc: { fontSize: '13px', color: '#888', marginTop: '6px' },
    stageCardActions: { display: 'flex', gap: '8px', flexShrink: 0 },
    stageEditBtn: { backgroundColor: '#2c2c2c', border: '1px solid #555', borderRadius: '4px', padding: '5px 12px', fontSize: '13px', color: '#ccc', cursor: 'pointer' },
    stageDeleteBtn: { backgroundColor: 'transparent', border: '1px solid #555', borderRadius: '4px', padding: '5px 12px', fontSize: '13px', color: '#cf6679', cursor: 'pointer' },
    stageFormTitle: { fontSize: '14px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    stageFormRow: { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '14px' },
    stageFormLabel: { fontSize: '13px', color: '#aaa', fontWeight: '600' },
    stageInput: { backgroundColor: '#1a1a1a', border: '1px solid #444', borderRadius: '6px', padding: '9px 12px', fontSize: '14px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    stageFormActions: { display: 'flex', gap: '10px', marginTop: '6px' },
    stageSaveBtn: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '8px 20px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
    stageCancelBtn: { backgroundColor: 'transparent', color: '#aaa', border: '1px solid #444', borderRadius: '4px', padding: '8px 16px', fontSize: '14px', cursor: 'pointer' },
    addStageBtn: { marginTop: '16px', backgroundColor: '#2c2c2c', border: '1px solid #555', borderRadius: '6px', padding: '9px 18px', fontSize: '14px', color: '#bb86fc', cursor: 'pointer', fontWeight: 'bold' },
    stageError: { backgroundColor: '#2a1a1a', border: '1px solid #cf6679', borderRadius: '6px', padding: '10px 14px', color: '#cf6679', fontSize: '13px', marginBottom: '16px' },

    // ── Flyer grid ──
    flyerControlsBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' },
    flyerFilterGroup: { display: 'flex', alignItems: 'center', gap: '6px', flex: '0 1 auto' },
    flyerFilterTypeSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '7px 10px', fontSize: '13px', color: '#e0e0e0', cursor: 'pointer', flexShrink: 0, width: '110px' },
    flyerFilterInput: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '7px 10px', fontSize: '13px', color: '#e0e0e0', width: '260px', outline: 'none' },
    flyerFilterClear: { background: 'transparent', border: 'none', color: '#666', fontSize: '14px', cursor: 'pointer', padding: '4px 6px', lineHeight: 1 },
    flyerSortGroup: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
    flyerSortLabel: { fontSize: '13px', color: '#888', whiteSpace: 'nowrap' },
    flyerSortSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '7px 10px', fontSize: '13px', color: '#e0e0e0', cursor: 'pointer' },
    flyerGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '8px' },
    flyerCell: { position: 'relative', display: 'block', aspectRatio: '8.5 / 11', overflow: 'hidden', borderRadius: '4px', border: '1px solid #2e2e2e', backgroundColor: '#111', textDecoration: 'none', cursor: 'pointer' },
    flyerCellPad: { aspectRatio: '8.5 / 11', visibility: 'hidden' },
    flyerImg: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block', transition: 'opacity 0.15s ease' },
    flyerOverlay: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)', padding: '20px 6px 5px', transition: 'opacity 0.15s ease' },
    flyerDate: { fontSize: '10px', color: '#e0e0e0', fontWeight: '600', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    flyerErrorBox: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: '#161616', border: '1px dashed #2e2e2e', borderRadius: '4px', padding: '12px' },
    flyerErrorIcon: { fontSize: '28px', opacity: 0.25 },
    flyerErrorDate: { fontSize: '10px', color: '#555', fontWeight: '600', textAlign: 'center', lineHeight: 1.4 },
    flyerErrorVenue: { fontSize: '10px', color: '#444', textAlign: 'center', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },

    // ── Analytics tab ──
    analyticsGrid: { display: 'flex', flexWrap: 'wrap', gap: '16px' },
    analyticsCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '24px 28px', textAlign: 'center', minWidth: '140px' },
    analyticsValue: { fontSize: '40px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    analyticsLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },
};

export default VenueDetail;
